import getConditionChildrenKey from 'editor/src/store/design/reducer/getConditionChildrenKey';
import {
  Condition,
  ConditionDisplayOption,
  ConditionGroup,
  conditionGroupChildenKey,
  MediaElement,
} from 'editor/src/store/design/types';

/**
 * File shape for a Condition exported by the seller-side ⋮ menu ("Export") and
 * read back by "Import". Deliberately carries no internal ids — a condition
 * exported from one design and imported into another must not collide with
 * either design's ids, so both the condition id and its option ids are always
 * regenerated on import.
 */
export const CONDITION_EXPORT_FORMAT = 'gelato-personalization-condition';
/** Legacy format: the condition's own fields only, nothing linked to it. Still read on import. */
export const CONDITION_EXPORT_VERSION = 1;
/** Current format written by Export: the condition plus everything linked to it (see below). */
export const CONDITION_EXPORT_VERSION_2 = 2;

export interface ExportedConditionOption {
  label: string;
  imageId?: string;
}

export interface ExportedConditionData {
  name: string;
  description?: string;
  displayOption: ConditionDisplayOption;
  showOnAllSpreads?: true;
  defaultOptionIndex: number;
  options: ExportedConditionOption[];
}

export interface ExportedConditionFile {
  format: typeof CONDITION_EXPORT_FORMAT;
  version: typeof CONDITION_EXPORT_VERSION;
  condition: ExportedConditionData;
}

/**
 * A single per-option text value in a "follows" entry, positioned by OPTION
 * INDEX (not id — a v2 file carries no ids). `null` marks an option the
 * author never wrote a value for (see TextConditionVariants.values, which
 * simply omits the key — an array needs an explicit hole instead).
 */
export type ExportedFollowsValue = { text: string; fontFamily?: string } | null;

/**
 * How one exported element's text tracks a condition (see
 * MediaText.extra.textVariants). `condition` is a PATH into the condition
 * tree being exported: '' is the root condition itself, and 'i.j' is the
 * j-th sub-condition nested under the i-th option, applied recursively
 * (e.g. '0.1' = root's option 0's 2nd sub-condition). This is enough to
 * re-target the text at import time, once every condition in the tree has a
 * freshly generated id.
 */
export interface ExportedFollows {
  condition: string;
  useOptionLabel?: true;
  /** Only meaningful when `useOptionLabel` is unset — see TextConditionVariants. */
  values?: ExportedFollowsValue[];
}

/**
 * One media element carried by the export, referenced by ARRAY INDEX from
 * wherever it is used (an option's `elements`, or nothing at all when it is
 * only reachable via a `follows` on itself). Deduplicated: an element used in
 * several places is still exported once.
 */
export interface ExportedConditionElement {
  /** The element's own JSON, minus `uuid` and anything else that only makes
   *  sense inside the source design (see stripElementForExport). */
  element: Record<string, unknown>;
  /** True when this element sat in the condition group's top-level ('top')
   *  bucket — a buyer-editable field, not gated by any option. */
  personalized?: true;
  /** Set when this element is a text whose `extra.textVariants` pointed at a
   *  condition in the exported tree. */
  follows?: ExportedFollows;
}

export interface ExportedConditionOptionV2 extends ExportedConditionOption {
  /** Indexes into the file's top-level `elements`, for media directly in this option's bucket. */
  elements: number[];
  /** Sub-conditions parented to this option, same shape as the root — recursive. */
  conditions: ExportedConditionDataV2[];
}

export interface ExportedConditionDataV2 extends Omit<ExportedConditionData, 'options'> {
  options: ExportedConditionOptionV2[];
}

export interface ExportedConditionFileV2 {
  format: typeof CONDITION_EXPORT_FORMAT;
  version: typeof CONDITION_EXPORT_VERSION_2;
  condition: ExportedConditionDataV2;
  elements: ExportedConditionElement[];
}

/** Filename for the downloaded export: sanitized condition name, or a fallback. */
export function conditionExportFilename(name: string): string {
  // Strip characters that are invalid (or awkward) in a filename on the
  // common filesystems; collapse the rest of the whitespace.
  const sanitized = name
    .replace(/[\\/:*?"<>|]+/g, '')
    .trim()
    .replace(/\s+/g, ' ');
  return sanitized ? `${sanitized}.json` : 'condition.json';
}

/**
 * Fields stripped from an exported element because they only mean something
 * inside the source design: `uuid` (regenerated on import), the text-variant
 * link (re-expressed as `follows`) and its cross-page value link, the
 * layout-frame id it was created from, which content box it belongs to, its
 * code-set column, and (for an image) a QR code's field-to-element mapping —
 * all references into a design the imported element will not be part of.
 */
function stripElementForExport(element: MediaElement): Record<string, unknown> {
  const { uuid, belongs_to, layoutFrameId, createdByLayout, ...rest } = element as unknown as Record<string, unknown>;
  const clone: Record<string, unknown> = { ...rest };

  if (clone.type === 'image' && 'qrCode' in clone) {
    delete clone.qrCode;
  }
  if (clone.type === 'text' && clone.extra && typeof clone.extra === 'object') {
    // textStyleId names a style of this design only (GAPI-61895); the text keeps its look.
    const { textVariants, sharedFieldId, variableColumn, textStyleId, ...restExtra } = clone.extra as Record<
      string,
      unknown
    >;
    clone.extra = restExtra;
    // It arrives as an ordinary buyer field, so the set's sample (and the outline
    // drawn from it) goes too, as on unbinding: left behind, it is a real-looking
    // code printed whenever the buyer types nothing.
    if (variableColumn) {
      clone.extra = { ...restExtra, text: '' };
      delete clone.path;
    }
  }
  return clone;
}

/**
 * Pure serializer: turns a live Condition — and everything linked to it on
 * its owning spread (sub-conditions, the elements shown per option, and any
 * text elsewhere on the spread whose text follows it) — into the portable v2
 * file shape. No ids: both the collected conditions and every option are
 * always regenerated on import, so nothing here can collide with the
 * importing design.
 */
export function serializeCondition(
  condition: Condition,
  conditionGroup: ConditionGroup,
  mediaElements: MediaElement[],
): ExportedConditionFileV2 {
  const elements: ExportedConditionElement[] = [];
  const elementIndexByUuid = new Map<number, number>();
  const pathByConditionId = new Map<string, string>();
  const optionIdsByConditionId = new Map<string, string[]>();

  // Undefined for a bucket child that names no element on the spread — stale
  // data a design can carry. Exporting it as `{}` would make the whole file
  // fail validation on import, so it is left out instead.
  function getOrAddElementIndex(uuid: number): number | undefined {
    const existing = elementIndexByUuid.get(uuid);
    if (existing !== undefined) {
      return existing;
    }
    const found = mediaElements.find((candidate) => candidate.uuid === uuid);
    if (!found) {
      return undefined;
    }
    const index = elements.length;
    elements.push({ element: stripElementForExport(found) });
    elementIndexByUuid.set(uuid, index);
    return index;
  }

  function collect(current: Condition, path: string): ExportedConditionDataV2 {
    pathByConditionId.set(current.id, path);
    optionIdsByConditionId.set(
      current.id,
      current.options.map((option) => option.id),
    );
    const activeIndex = current.options.findIndex((option) => option.id === current.activeOptionId);

    return {
      name: current.name,
      ...(current.description ? { description: current.description } : {}),
      displayOption: current.displayOption,
      ...(current.showOnAllSpreads ? { showOnAllSpreads: true as const } : {}),
      defaultOptionIndex: activeIndex >= 0 ? activeIndex : 0,
      options: current.options.map((option, optionIndex) => {
        const bucket = conditionGroup.children[getConditionChildrenKey(current.id, option.id)] ?? [];
        const elementRefs: number[] = [];
        const subConditions: ExportedConditionDataV2[] = [];

        bucket.forEach((child) => {
          if (child.type === 'condition') {
            const sub = conditionGroup.conditions[child.id];
            if (sub) {
              const subPath = `${path ? `${path}.` : ''}${optionIndex}.${subConditions.length}`;
              subConditions.push(collect(sub, subPath));
            }
          } else {
            const elementIndex = getOrAddElementIndex(child.id);
            if (elementIndex !== undefined) {
              elementRefs.push(elementIndex);
            }
          }
        });

        return {
          label: option.label,
          ...(option.imageId ? { imageId: option.imageId } : {}),
          elements: elementRefs,
          conditions: subConditions,
        };
      }),
    };
  }

  const root = collect(condition, '');
  const collectedConditionIds = new Set(pathByConditionId.keys());
  const topBucketUuids = new Set(
    (conditionGroup.children[conditionGroupChildenKey] ?? [])
      .filter((child) => child.type !== 'condition')
      .map((child) => child.id as number),
  );

  // Following texts (rule 3): anywhere on the spread, whether or not they are
  // also in a bucket — a personalized ('top') field with per-option text is
  // exactly that: reachable via `personalized`, not via any option's `elements`.
  mediaElements.forEach((element) => {
    if (element.type !== 'text') {
      return;
    }
    const variants = element.extra.textVariants;
    if (!variants || !collectedConditionIds.has(variants.conditionId)) {
      return;
    }
    const path = pathByConditionId.get(variants.conditionId)!;
    // Always found: `element` comes from mediaElements itself.
    const entry = elements[getOrAddElementIndex(element.uuid)!];

    if (topBucketUuids.has(element.uuid)) {
      entry.personalized = true;
    }

    if (variants.useOptionLabel) {
      entry.follows = { condition: path, useOptionLabel: true };
    } else {
      const optionIds = optionIdsByConditionId.get(variants.conditionId) ?? [];
      entry.follows = {
        condition: path,
        values: optionIds.map((optionId) => {
          const value = variants.values[optionId];
          return value ? { text: value.text, ...(value.fontFamily ? { fontFamily: value.fontFamily } : {}) } : null;
        }),
      };
    }
  });

  return {
    format: CONDITION_EXPORT_FORMAT,
    version: CONDITION_EXPORT_VERSION_2,
    condition: root,
    elements,
  };
}

export type ConditionImportValidationResult =
  | { valid: true; version: typeof CONDITION_EXPORT_VERSION; condition: ExportedConditionData }
  | {
      valid: true;
      version: typeof CONDITION_EXPORT_VERSION_2;
      condition: ExportedConditionDataV2;
      elements: ExportedConditionElement[];
    }
  | { valid: false; reason: 'format' | 'name' | 'displayOption' | 'options' | 'elements' };

const VALID_DISPLAY_OPTIONS: string[] = Object.values(ConditionDisplayOption);
const VALID_ELEMENT_TYPES = ['addon', 'image', 'text', 'grid', 'line', 'rectangle'];

interface ParsedConditionCommon {
  name: string;
  description?: string;
  displayOption: ConditionDisplayOption;
  showOnAllSpreads?: true;
  defaultOptionIndexRaw: unknown;
}

/** Shared field checks between v1 and v2 — everything but `options`. */
function parseConditionCommon(raw: Record<string, unknown>): ParsedConditionCommon | undefined {
  if (typeof raw.name !== 'string' || raw.name.trim() === '') {
    return undefined;
  }
  if (typeof raw.displayOption !== 'string' || !VALID_DISPLAY_OPTIONS.includes(raw.displayOption)) {
    return undefined;
  }
  return {
    name: raw.name,
    ...(typeof raw.description === 'string' ? { description: raw.description } : {}),
    displayOption: raw.displayOption as ConditionDisplayOption,
    ...(raw.showOnAllSpreads === true ? { showOnAllSpreads: true as const } : {}),
    defaultOptionIndexRaw: raw.defaultOptionIndex,
  };
}

function clampDefaultOptionIndex(raw: unknown, optionsLength: number): number {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw < optionsLength ? raw : 0;
}

/**
 * Validates and normalizes a parsed JSON file against the exported shape.
 * Pure and defensive — the input is untrusted (a file picked by the author,
 * possibly hand-edited or from an unrelated tool) so every field is checked
 * before use; unknown/extra fields are ignored rather than rejected, and a
 * present-but-out-of-range `defaultOptionIndex` falls back to 0 rather than
 * failing the whole file. Accepts both the legacy v1 shape (the condition's
 * own fields only) and the current v2 shape (the condition plus everything
 * linked to it).
 */
export function validateImportedConditionFile(data: unknown): ConditionImportValidationResult {
  if (!data || typeof data !== 'object') {
    return { valid: false, reason: 'format' };
  }
  const file = data as Record<string, unknown>;
  if (file.format !== CONDITION_EXPORT_FORMAT) {
    return { valid: false, reason: 'format' };
  }
  if (!file.condition || typeof file.condition !== 'object') {
    return { valid: false, reason: 'format' };
  }

  if (file.version === CONDITION_EXPORT_VERSION) {
    return validateV1(file.condition as Record<string, unknown>);
  }
  if (file.version === CONDITION_EXPORT_VERSION_2) {
    return validateV2(file.condition as Record<string, unknown>, file.elements);
  }
  return { valid: false, reason: 'format' };
}

function validateOptionBase(rawOption: unknown): { label: string; imageId?: string } | undefined {
  if (!rawOption || typeof rawOption !== 'object') {
    return undefined;
  }
  const option = rawOption as Record<string, unknown>;
  if (
    typeof option.label !== 'string' ||
    option.label.trim() === '' ||
    (option.imageId !== undefined && typeof option.imageId !== 'string')
  ) {
    return undefined;
  }
  return { label: option.label, ...(typeof option.imageId === 'string' ? { imageId: option.imageId } : {}) };
}

function validateV1(raw: Record<string, unknown>): ConditionImportValidationResult {
  const common = parseConditionCommon(raw);
  if (!common) {
    return { valid: false, reason: typeof raw.name !== 'string' || raw.name.trim() === '' ? 'name' : 'displayOption' };
  }

  if (!Array.isArray(raw.options) || raw.options.length < 2) {
    return { valid: false, reason: 'options' };
  }
  const options: ExportedConditionOption[] = [];
  const allOptionsValid = raw.options.every((rawOption) => {
    const option = validateOptionBase(rawOption);
    if (!option) {
      return false;
    }
    options.push(option);
    return true;
  });
  if (!allOptionsValid) {
    return { valid: false, reason: 'options' };
  }

  return {
    valid: true,
    version: CONDITION_EXPORT_VERSION,
    condition: {
      name: common.name,
      ...(common.description ? { description: common.description } : {}),
      displayOption: common.displayOption,
      ...(common.showOnAllSpreads ? { showOnAllSpreads: true as const } : {}),
      defaultOptionIndex: clampDefaultOptionIndex(common.defaultOptionIndexRaw, options.length),
      options,
    },
  };
}

/** Recursively validates one condition node of a v2 tree (root or sub-condition). */
function validateV2ConditionTree(
  raw: Record<string, unknown>,
  elementsLength: number,
): ExportedConditionDataV2 | undefined {
  const common = parseConditionCommon(raw);
  if (!common) {
    return undefined;
  }
  if (!Array.isArray(raw.options) || raw.options.length < 2) {
    return undefined;
  }

  const options: ExportedConditionOptionV2[] = [];
  const allOptionsValid = raw.options.every((rawOption) => {
    const base = validateOptionBase(rawOption);
    if (!base || !rawOption || typeof rawOption !== 'object') {
      return false;
    }
    const option = rawOption as Record<string, unknown>;

    const rawElements = option.elements;
    if (
      !Array.isArray(rawElements) ||
      !rawElements.every((ref) => Number.isInteger(ref) && ref >= 0 && ref < elementsLength)
    ) {
      return false;
    }

    const rawConditions = option.conditions;
    if (!Array.isArray(rawConditions)) {
      return false;
    }
    const conditions: ExportedConditionDataV2[] = [];
    const allSubConditionsValid = rawConditions.every((rawSub) => {
      if (!rawSub || typeof rawSub !== 'object') {
        return false;
      }
      const sub = validateV2ConditionTree(rawSub as Record<string, unknown>, elementsLength);
      if (!sub) {
        return false;
      }
      conditions.push(sub);
      return true;
    });
    if (!allSubConditionsValid) {
      return false;
    }

    options.push({ ...base, elements: rawElements as number[], conditions });
    return true;
  });
  if (!allOptionsValid) {
    return undefined;
  }

  return {
    name: common.name,
    ...(common.description ? { description: common.description } : {}),
    displayOption: common.displayOption,
    ...(common.showOnAllSpreads ? { showOnAllSpreads: true as const } : {}),
    defaultOptionIndex: clampDefaultOptionIndex(common.defaultOptionIndexRaw, options.length),
    options,
  };
}

/** Every path 'condition' string that actually occurs in the validated tree, mapped to its option count. */
function collectPathOptionCounts(condition: ExportedConditionDataV2, path: string, out: Map<string, number>): void {
  out.set(path, condition.options.length);
  condition.options.forEach((option, optionIndex) => {
    option.conditions.forEach((sub, subIndex) => {
      collectPathOptionCounts(sub, `${path ? `${path}.` : ''}${optionIndex}.${subIndex}`, out);
    });
  });
}

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object';

/**
 * The fields the editor reads unconditionally when it measures and renders an
 * element of each type. An imported element is inserted into the design as-is,
 * so one missing here (a hand-edited or truncated file) would crash the canvas
 * after the import is already in the store — reject the file instead.
 */
function hasRequiredElementFields(element: Record<string, unknown>): boolean {
  if (element.type === 'line') {
    return [element.x1, element.y1, element.x2, element.y2, element.strokeWidth].every(isNumber);
  }
  if (![element.x, element.y, element.width, element.height].every(isNumber)) {
    return false;
  }
  switch (element.type) {
    case 'text':
      return (
        isObject(element.extra) &&
        typeof element.extra.text === 'string' &&
        typeof element.extra.fontFamily === 'string' &&
        isNumber(element.extra.fontSize)
      );
    case 'addon':
      return typeof element.addonId === 'string' && typeof element.url === 'string';
    case 'grid':
      return typeof element.content === 'string' && isObject(element.grid);
    default:
      return true;
  }
}

function validateV2Elements(
  rawElements: unknown,
  pathOptionCounts: Map<string, number>,
): ExportedConditionElement[] | undefined {
  if (!Array.isArray(rawElements)) {
    return undefined;
  }

  function parseValues(rawValues: unknown[]): ExportedFollowsValue[] | undefined {
    const values: ExportedFollowsValue[] = [];
    const allValid = rawValues.every((rawValue) => {
      if (rawValue === null) {
        values.push(null);
        return true;
      }
      if (!rawValue || typeof rawValue !== 'object') {
        return false;
      }
      const value = rawValue as Record<string, unknown>;
      if (typeof value.text !== 'string' || (value.fontFamily !== undefined && typeof value.fontFamily !== 'string')) {
        return false;
      }
      values.push({
        text: value.text,
        ...(typeof value.fontFamily === 'string' ? { fontFamily: value.fontFamily } : {}),
      });
      return true;
    });
    return allValid ? values : undefined;
  }

  function parseElement(rawElement: unknown): ExportedConditionElement | undefined {
    if (!rawElement || typeof rawElement !== 'object') {
      return undefined;
    }
    const entry = rawElement as Record<string, unknown>;
    if (!entry.element || typeof entry.element !== 'object') {
      return undefined;
    }
    const element = entry.element as Record<string, unknown>;
    if (
      typeof element.type !== 'string' ||
      !VALID_ELEMENT_TYPES.includes(element.type) ||
      !hasRequiredElementFields(element)
    ) {
      return undefined;
    }

    const parsed: ExportedConditionElement = { element };
    if (entry.personalized === true) {
      parsed.personalized = true;
    }

    if (entry.follows !== undefined) {
      if (!entry.follows || typeof entry.follows !== 'object') {
        return undefined;
      }
      const follows = entry.follows as Record<string, unknown>;
      if (typeof follows.condition !== 'string' || !pathOptionCounts.has(follows.condition)) {
        return undefined;
      }
      const optionCount = pathOptionCounts.get(follows.condition)!;

      if (follows.useOptionLabel === true) {
        parsed.follows = { condition: follows.condition, useOptionLabel: true };
      } else {
        if (!Array.isArray(follows.values) || follows.values.length > optionCount) {
          return undefined;
        }
        const values = parseValues(follows.values);
        if (!values) {
          return undefined;
        }
        parsed.follows = { condition: follows.condition, values };
      }
    }

    return parsed;
  }

  const elements: ExportedConditionElement[] = [];
  const allElementsValid = rawElements.every((rawElement) => {
    const parsed = parseElement(rawElement);
    if (!parsed) {
      return false;
    }
    elements.push(parsed);
    return true;
  });

  return allElementsValid ? elements : undefined;
}

function validateV2(raw: Record<string, unknown>, rawElements: unknown): ConditionImportValidationResult {
  // Element refs are checked for range against the elements array's length,
  // so it must already be a well-formed array before the tree is walked.
  const elementsLength = Array.isArray(rawElements) ? rawElements.length : 0;
  if (!Array.isArray(rawElements)) {
    return { valid: false, reason: 'elements' };
  }

  const condition = validateV2ConditionTree(raw, elementsLength);
  if (!condition) {
    if (typeof raw.name !== 'string' || raw.name.trim() === '') {
      return { valid: false, reason: 'name' };
    }
    if (typeof raw.displayOption === 'string' && VALID_DISPLAY_OPTIONS.includes(raw.displayOption)) {
      return { valid: false, reason: 'options' };
    }
    return { valid: false, reason: 'displayOption' };
  }

  const pathOptionCounts = new Map<string, number>();
  collectPathOptionCounts(condition, '', pathOptionCounts);

  const elements = validateV2Elements(rawElements, pathOptionCounts);
  if (!elements) {
    return { valid: false, reason: 'elements' };
  }

  return { valid: true, version: CONDITION_EXPORT_VERSION_2, condition, elements };
}
